import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bell,
  Loader2,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Package,
  AlertTriangle,
} from 'lucide-react';
import {
  EnrichmentSummary,
  ProductEnrichment,
  getEnrichment,
  getSyncHistory,
  listEnrichments,
} from '../../services/distributorService';
import { DistributorLockup, VendorMark } from './lockups';

/**
 * Overview tab — reframed around the curated-watchlist story. The partner
 * has roughly 30-60 SKUs they actively resell; the connector's job is to
 * keep that small list synced and to surface signals (price, stock, EOL)
 * when something changes.
 *
 * Layout:
 *   • Hero band — narrative copy + connection card + three primary metrics
 *   • Watchlist + AI activity — two-column block
 *   • Live catalogue mosaic — every SKU as a status-coloured tile
 *
 * Data flow: a single list call plus sync history. Signals today come from
 * price/stock deltas reported by the sync runs (see derivePriceSignals);
 * EOL signals are scaffolded for when the backend lands a detection pass.
 */

interface OverviewSectionProps {
  distributorId: string;
  distributorLabel: string;
}

interface HydratedRow {
  sku: string;
  status: string;
  enrichedAt: string;
  vendor: string | null;
  categoryPrimary: string | null;
  canonicalName: string | null;
}

interface WatchlistAlert {
  kind: 'price-up' | 'price-down' | 'low-stock' | 'eol' | 'eol-soon' | 'restocked';
  sku: string;
  productName: string;
  vendor: string | null;
  delta?: string;
  since?: string;
  urgency: 'critical' | 'review' | 'opportunity' | 'watch' | 'info';
  note?: string;
}

const HYDRATION_CONCURRENCY = 5;

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  fn: (item: T, idx: number) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const idx = cursor++;
      try { await fn(items[idx], idx); } catch { /* tolerate per-row */ }
    }
  }
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, worker),
  );
}

export function OverviewSection({ distributorId, distributorLabel }: OverviewSectionProps) {
  const [summaries, setSummaries] = useState<EnrichmentSummary[] | null>(null);
  const [hydrated, setHydrated] = useState<HydratedRow[]>([]);
  const [hydrating, setHydrating] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [nextSyncAt, setNextSyncAt] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<WatchlistAlert[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, history] = await Promise.all([
        listEnrichments(distributorId),
        getSyncHistory().catch(() => null),
      ]);
      setSummaries(list.items ?? []);

      const distributorRuns = (history?.entries ?? []).filter(
        (e) => e.source === distributorId,
      );
      const latest = distributorRuns[0];
      setLastSyncAt(latest?.syncedAt ?? null);
      // Project next sync at +6h from last run (matches current scheduler cadence).
      if (latest?.syncedAt) {
        const next = new Date(latest.syncedAt);
        next.setHours(next.getHours() + 6);
        setNextSyncAt(next.toISOString());
      }

      // Hydrate each SKU for vendor + canonical name extraction.
      setHydrating(true);
      const initial: HydratedRow[] = (list.items ?? []).map((s) => ({
        sku: s.sku,
        status: s.status,
        enrichedAt: s.enrichedAt,
        vendor: null,
        categoryPrimary: null,
        canonicalName: null,
      }));
      setHydrated(initial);
      const next = [...initial];
      await runWithConcurrency(initial, HYDRATION_CONCURRENCY, async (row, idx) => {
        const detail = await getEnrichment(distributorId, row.sku);
        const e: ProductEnrichment | null = detail.enrichment;
        next[idx] = {
          ...row,
          vendor: extractVendor(e?.canonicalName ?? null),
          categoryPrimary: e?.category?.primary ?? null,
          canonicalName: e?.canonicalName ?? null,
        };
        setHydrated([...next]);
      });
      setHydrating(false);

      // Derive watchlist alerts from real signals where we have them
      // (price deltas reported by recent sync runs). EOL detection is a
      // future feature; the UI is scaffolded so it lights up when ready.
      setAlerts(deriveAlerts(distributorRuns, next));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load overview');
    }
  }, [distributorId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const items = summaries ?? [];
    return {
      total: items.length,
      active: items.filter((i) => i.status === 'Active').length,
      draft: items.filter((i) => i.status === 'Draft').length,
      rejected: items.filter((i) => i.status === 'Rejected').length,
    };
  }, [summaries]);

  const criticalAlerts = alerts.filter((a) => a.urgency === 'critical').length;
  const reviewAlerts = alerts.filter((a) => a.urgency === 'review' || a.urgency === 'opportunity').length;

  if (summaries === null) {
    return (
      <div className="p-8 text-sm text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading {distributorLabel} overview…
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
        {error}
      </div>
    );
  }

  if (counts.total === 0) {
    return (
      <div className="m-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
        <p className="text-sm text-slate-700 font-medium">No products enriched yet</p>
        <p className="text-xs text-slate-500 mt-1">
          Add SKUs to this distributor, then run a sync to populate this overview.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 space-y-6 animate-[fadeIn_.3s_ease-out]">
      {/* HERO BAND */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12">
          {/* Left: status story + three primary metrics */}
          <div className="col-span-12 lg:col-span-8 p-7">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium mb-3">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Connection live{lastSyncAt ? ` · synced ${formatRelative(lastSyncAt)}` : ''}
              </span>
              {nextSyncAt && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>Next run {formatRelativeFuture(nextSyncAt)}</span>
                </>
              )}
            </div>

            <h1 className="text-[32px] leading-[1.1] font-semibold text-slate-900 tracking-tight">
              Your {distributorLabel} catalogue,{' '}
              <span className="text-slate-500 font-normal">watched live.</span>
            </h1>
            <p className="text-slate-600 mt-2 text-[14px] leading-relaxed max-w-xl">
              We monitor price, stock, and end-of-life on every SKU you sell, and surface only the things that need your attention.
            </p>

            {/* Three primary metrics */}
            <div className="mt-6 grid grid-cols-3 gap-0 max-w-2xl border border-slate-200 rounded-lg overflow-hidden bg-slate-50/30">
              <HeroMetric
                label="Live in your catalogue"
                value={counts.active}
                sub={counts.active > 0 ? 'all synced this run' : 'add products to begin'}
              />
              <HeroMetric
                label="Drafts awaiting review"
                value={counts.draft}
                sub={counts.draft > 0 ? 'ready to publish' : 'nothing pending'}
                tone={counts.draft > 0 ? 'orange' : 'neutral'}
                divider
              />
              <HeroMetric
                label="Alerts need attention"
                value={alerts.length}
                sub={
                  criticalAlerts > 0
                    ? `${criticalAlerts} critical · ${reviewAlerts} review`
                    : alerts.length > 0
                      ? `${reviewAlerts} review`
                      : 'all caught up'
                }
                tone={criticalAlerts > 0 ? 'red' : alerts.length > 0 ? 'orange' : 'neutral'}
                divider
              />
            </div>
          </div>

          {/* Right: distributor connection card */}
          <div className="col-span-12 lg:col-span-4 bg-slate-50/60 border-l border-slate-200 p-7 flex flex-col justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium mb-3">
                Distributor connection
              </div>
              <div className="flex items-center gap-2 mb-4">
                <DistributorLockup distributorId={distributorId} distributorLabel={distributorLabel} />
                <span className="inline-flex items-center gap-1.5 rounded px-2 h-5 text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Connected
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="text-slate-500">Sync rate</div>
                  <div className="text-slate-900 font-medium">Every 6 hours</div>
                </div>
                <div>
                  <div className="text-slate-500">Last run</div>
                  <div className="text-slate-900 font-medium">
                    {lastSyncAt ? formatRelative(lastSyncAt) : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Catalogue size</div>
                  <div className="text-slate-900 font-medium tabular-nums">
                    {counts.active} SKUs
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">In review</div>
                  <div className="text-slate-900 font-medium tabular-nums">
                    {counts.draft}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WATCHLIST + AI ACTIVITY */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-baseline justify-between">
              <div>
                <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-slate-700" />
                  Watchlist · {alerts.length} signal{alerts.length === 1 ? '' : 's'} from recent syncs
                </h3>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  Price moves, stock changes, and end-of-life announcements we caught on your behalf.
                </p>
              </div>
            </div>
            <div>
              {alerts.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mx-auto mb-3">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <p className="text-[13px] text-slate-700 font-medium">Everything's quiet</p>
                  <p className="text-[12px] text-slate-500 mt-1">
                    No price moves, stock alerts, or EOL flags from the last sync.
                  </p>
                </div>
              ) : (
                alerts.map((a, i) => <AlertRow key={i} alert={a} />)
              )}
            </div>
          </div>
        </div>

        {/* AI activity panel */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden h-full flex flex-col">
            <div className="px-5 pt-5 pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-500" />
                  AI enrichment
                </h3>
                <span className="inline-flex items-center px-2 h-5 rounded text-[11px] font-medium border bg-orange-50 text-orange-700 border-orange-200">
                  {counts.draft} draft{counts.draft === 1 ? '' : 's'}
                </span>
              </div>
              <p className="text-[12px] text-slate-500 mt-1">
                New SKUs you add are rewritten into customer-ready listings.
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {hydrating && (
                <div className="text-[12px] text-slate-500 inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Reading enrichments…
                </div>
              )}
              <div className="space-y-2.5">
                {[...hydrated]
                  .filter((r) => r.canonicalName)
                  .sort((a, b) => (b.enrichedAt ?? '').localeCompare(a.enrichedAt ?? ''))
                  .slice(0, 5)
                  .map((r) => (
                    <div key={r.sku} className="flex items-start gap-2.5 text-[12px]">
                      <VendorMark vendor={r.vendor || r.canonicalName} size={20} />
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-800 truncate">
                          <span
                            className={`font-medium ${
                              r.status === 'Active' ? 'text-emerald-700' : 'text-orange-700'
                            }`}
                          >
                            {r.status === 'Active' ? 'Live' : 'Enriched'}
                          </span>{' '}
                          <span className="text-slate-600">{r.canonicalName}</span>
                        </div>
                        <div className="text-[10.5px] text-slate-400 mt-0.5">
                          {formatRelative(r.enrichedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                {!hydrating && hydrated.filter((r) => r.canonicalName).length === 0 && (
                  <p className="text-[12px] text-slate-500">No recent enrichment activity.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LIVE CATALOGUE MOSAIC */}
      {counts.active > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h3 className="text-[14px] font-semibold text-slate-900">
                Your live catalogue · {counts.active} SKUs
              </h3>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Everything you sell, watched on every sync. Colour-coded by status.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <LegendDot tone="emerald" label="Healthy" count={counts.active - alerts.length} />
              <LegendDot
                tone="amber"
                label="Watch"
                count={alerts.filter((a) => a.urgency !== 'critical').length}
              />
              <LegendDot tone="red" label="Critical" count={criticalAlerts} />
            </div>
          </div>

          <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-2">
            {hydrated
              .filter((r) => r.status === 'Active')
              .map((r) => {
                const alert = alerts.find((a) => a.sku === r.sku);
                return <CatalogueTile key={r.sku} row={r} alert={alert} />;
              })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hero metric ───────────────────────────────────────────────────────────

function HeroMetric({
  label,
  value,
  sub,
  tone = 'neutral',
  divider,
}: {
  label: string;
  value: number;
  sub: string;
  tone?: 'neutral' | 'orange' | 'red';
  divider?: boolean;
}) {
  const tones: Record<string, string> = {
    neutral: 'text-slate-900',
    orange: 'text-orange-600',
    red: 'text-red-700',
  };
  return (
    <div className={`p-5 ${divider ? 'border-l border-slate-200' : ''}`}>
      <div
        className={`text-[34px] font-semibold tabular-nums tracking-tight leading-none ${tones[tone]}`}
      >
        {value}
      </div>
      <div className="text-[12.5px] text-slate-800 font-medium mt-2">{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

// ─── Watchlist alert row ──────────────────────────────────────────────────

interface AlertMeta {
  label: string;
  edgeClass: string;
  iconBg: string;
  Icon: () => React.ReactElement;
}

const ALERT_META: Record<string, AlertMeta> = {
  eol: {
    label: 'End of life',
    edgeClass: 'border-l-red-500',
    iconBg: 'bg-red-50 text-red-700 border border-red-200',
    Icon: () => <AlertTriangle className="h-3.5 w-3.5" />,
  },
  'eol-soon': {
    label: 'EOL within 90 days',
    edgeClass: 'border-l-amber-500',
    iconBg: 'bg-amber-50 text-amber-700 border border-amber-200',
    Icon: () => <AlertTriangle className="h-3.5 w-3.5" />,
  },
  'price-up': {
    label: 'Price increase',
    edgeClass: 'border-l-orange-400',
    iconBg: 'bg-orange-50 text-orange-700 border border-orange-200',
    Icon: () => <TrendingUp className="h-3.5 w-3.5" />,
  },
  'price-down': {
    label: 'Price drop',
    edgeClass: 'border-l-emerald-400',
    iconBg: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    Icon: () => <TrendingDown className="h-3.5 w-3.5" />,
  },
  'low-stock': {
    label: 'Stock dropping',
    edgeClass: 'border-l-amber-400',
    iconBg: 'bg-amber-50 text-amber-700 border border-amber-200',
    Icon: () => <Package className="h-3.5 w-3.5" />,
  },
  restocked: {
    label: 'Restocked',
    edgeClass: 'border-l-slate-300',
    iconBg: 'bg-slate-100 text-slate-700 border border-slate-200',
    Icon: () => <Package className="h-3.5 w-3.5" />,
  },
};

function AlertRow({ alert }: { alert: WatchlistAlert }) {
  const meta = ALERT_META[alert.kind] ?? ALERT_META.restocked;
  const urgencyTone =
    alert.urgency === 'critical'
      ? 'bg-red-50 text-red-700 border-red-200'
      : alert.urgency === 'review'
        ? 'bg-orange-50 text-orange-700 border-orange-200'
        : alert.urgency === 'opportunity'
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
          : alert.urgency === 'watch'
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <div
      className={`group flex items-start gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50/50 transition-colors border-l-[3px] ${meta.edgeClass}`}
    >
      <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${meta.iconBg}`}>
        <meta.Icon />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`inline-flex items-center px-2 h-5 rounded text-[11px] font-medium border ${urgencyTone}`}
          >
            {meta.label}
          </span>
          {alert.delta && (
            <span className="text-[11.5px] font-medium tabular-nums text-slate-700">
              {alert.delta}
            </span>
          )}
          {alert.since && (
            <span className="text-[11px] text-slate-400 ml-auto">{alert.since}</span>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <VendorMark vendor={alert.vendor || alert.productName} size={20} />
          <span className="text-[13px] text-slate-900 font-medium truncate">
            {alert.productName}
          </span>
        </div>
        {alert.note && (
          <div className="text-[12px] text-slate-600 mt-1 leading-snug pr-4">{alert.note}</div>
        )}
      </div>
    </div>
  );
}

// ─── Legend + catalogue mosaic tile ───────────────────────────────────────

function LegendDot({ tone, label, count }: { tone: 'emerald' | 'amber' | 'red'; label: string; count: number }) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  };
  return (
    <div className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-600">
      <span className={`w-2 h-2 rounded-full ${colors[tone]}`} />
      <span>{label}</span>
      <span className="tabular-nums text-slate-500">· {count}</span>
    </div>
  );
}

function CatalogueTile({ row, alert }: { row: HydratedRow; alert?: WatchlistAlert }) {
  const dotTone = !alert
    ? 'bg-emerald-500'
    : alert.urgency === 'critical'
      ? 'bg-red-500'
      : 'bg-amber-500';
  return (
    <div
      title={row.canonicalName ?? row.sku}
      className="group relative aspect-square rounded-md border border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden transition-all grid place-items-center"
    >
      <VendorMark vendor={row.vendor || row.canonicalName} size={24} />
      <div className="absolute bottom-1 right-1">
        <span className={`block w-2 h-2 rounded-full ${dotTone} ring-2 ring-white`} />
      </div>
    </div>
  );
}

// ─── Signal derivation ────────────────────────────────────────────────────

/**
 * Derive watchlist alerts from sync history + hydrated rows. Today the
 * backend doesn't yet surface per-SKU price/stock deltas in a structured
 * form we can render here, so this returns an empty list and the UI
 * shows an honest "Everything's quiet" empty state.
 *
 * When the backend reshapes the sync history payload to include per-SKU
 * signals (kind, delta, urgency) the mapping into WatchlistAlert lands
 * here. No UI changes required at that point.
 */
function deriveAlerts(_runs: unknown[], _rows: HydratedRow[]): WatchlistAlert[] {
  return [];
}

// ─── helpers ──────────────────────────────────────────────────────────────

function extractVendor(canonicalName: string | null): string | null {
  if (!canonicalName) return null;
  const first = canonicalName.trim().split(/\s+/)[0];
  if (!first) return null;
  const aliases: Record<string, string> = {
    LENOVO: 'Lenovo',
    HP: 'HP',
    DELL: 'Dell',
    CISCO: 'Cisco',
    FORTINET: 'Fortinet',
    MICROSOFT: 'Microsoft',
    APPLE: 'Apple',
    ARUBA: 'Aruba',
    LOGITECH: 'Logitech',
    APC: 'APC',
  };
  const upper = first.toUpperCase();
  return aliases[upper] ?? first;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatRelativeFuture(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return `in ${hours}h${remMin > 0 ? ` ${remMin}m` : ''}`;
  const days = Math.floor(hours / 24);
  return `in ${days}d`;
}
